<template>
    <form
        ref="formEl"
        :action="submitUrl"
        method="post"
        class="lfmpub-form"
        :class="'lfmpub-form--' + config.mode"
        @submit.prevent="handleSubmit"
        novalidate
    >
        <!-- Hidden field carrying the JSON payload for the PHP mapper -->
        <input type="hidden" name="tx_lfmpublications_pubform[publicationData]" :value="serializedData">

        <!-- Type selector row -->
        <div class="grid-container">
            <div class="container">
                <div class="lfmpub-form__type-row">
                    <TypeSelect
                        :model-value="form.type"
                        :types="config.payload.types"
                        @update:model-value="onTypeChange"
                    />
                </div>
            </div>
        </div>

        <!-- Main form content -->
        <div class="grid-container grid-container--background-gray-small">
            <div class="container">
                <!-- Type display label -->
                <div class="mb-15">
                    <div class="h4 text-center">{{ currentTypeName }}</div>
                </div>

                <BoxTitleData
                    :form="form"
                    :current-type="form.type"
                    :payload="config.payload"
                    :check-title-url="isNew ? config.urls.checkTitle : ''"
                    :is-new="isNew"
                    :confirm-modal="confirmModal"
                />

                <BoxMediaTypesKeywords
                    :form="form"
                    :payload="config.payload"
                    :keyword-search-url="config.urls.keywordSearch"
                />

                <BoxPublisher
                    :form="form"
                    :current-type="form.type"
                    :payload="config.payload"
                    :check-isbn-url="isNew ? config.urls.checkIsbn : ''"
                    :is-new="isNew"
                    :confirm-modal="confirmModal"
                />

                <div ref="personsSectionEl">
                    <BoxPersons
                        :form="form"
                        :payload="config.payload"
                    />
                </div>

                <BoxFurtherData
                    :form="form"
                    :payload="config.payload"
                    :current-type="form.type"
                />

                <!-- Action buttons -->
                <div class="justify-content-center d-flex flex-wrap flex-lg-nowrap gap-5 gap-lg-10">
                    <button type="submit" class="button button--primary" :disabled="submitting">
                        {{ submitting ? 'Wird gespeichert...' : 'Daten übernehmen' }}
                    </button>

                    <a :href="config.urls.cancel" class="button button--white">
                        Abbrechen
                    </a>

                    <button
                        v-if="config.mode === 'edit'"
                        type="button"
                        class="button button--danger"
                        @click="handleDelete"
                    >
                        Datensatz löschen
                    </button>
                </div>
            </div>
        </div>

        <ConfirmModal ref="confirmModal" />
    </form>
</template>

<script setup>
import {ref, reactive, computed, provide, nextTick, onMounted, watch} from 'vue';
import $ from 'jquery';
import 'parsleyjs';
import '../../../Language/parsley';
import TypeSelect from './components/TypeSelect.vue';
import BoxTitleData from './components/BoxTitleData.vue';
import BoxMediaTypesKeywords from './components/BoxMediaTypesKeywords.vue';
import BoxPublisher from './components/BoxPublisher.vue';
import BoxPersons from './components/BoxPersons.vue';
import BoxFurtherData from './components/BoxFurtherData.vue';
import ConfirmModal from './components/ConfirmModal.vue';
import {PublicationType} from './publicationTypes';

// --- Read config from the <script type="application/json"> block ---
const configEl = document.getElementById('lfmpub-form-config');
const config = JSON.parse(configEl?.textContent || '{}');
const isNew = config.mode === 'new';

// --- Form state ---
const pub = config.publication || {};

const form = reactive({
    uid: pub.uid || null,
    type: pub.type || PublicationType.Monographie,
    title: pub.title || '',
    subtitle: pub.subtitle || '',
    parallelTitle: pub.parallelTitle || '',
    favor: pub.favor || '',
    uniformTitle: pub.uniformTitle || '',
    secondaryTitle: pub.secondaryTitle || '',
    otherTitle: pub.otherTitle || '',
    series: pub.series || null,
    volume: pub.volume || 0,
    magazine: pub.magazine || null,
    magazineBook: pub.magazineBook || '',
    magazineYear: pub.magazineYear || '',
    publication: pub.publication || null,
    publisher: pub.publisher || null,
    // Inline creation of a new Verlag — name and Erscheinungsort both live on the publisher record
    _publisherCreateNew: false,
    _publisherName: '',
    _publisherLocation: '',
    isbn: pub.isbn || '',
    language: pub.language || null,
    pages: pub.pages || 0,
    year: pub.year || '',
    url: pub.url || '',
    url2: pub.url2 || '',
    url2Addition: pub.url2Addition || '',
    universityWriting: pub.universityWriting || '',
    editionType: pub.editionType || null,
    edition: pub.edition || '',
    doi: pub.doi || '',
    urn: pub.urn || '',
    issn: pub.issn || '',
    abstract: pub.abstract || '',
    attached: pub.attached || '',
    attachedWork: pub.attachedWork || '',
    addionalInformation: pub.addionalInformation || '',
    onlyWaldorf: pub.onlyWaldorf || false,
    // Collections
    mediaTypes: pub.mediaTypes?.length ? [...pub.mediaTypes] : [null],
    keywords: pub.keywords?.length ? [...pub.keywords] : [null],
    persons: pub.persons?.length
        ? pub.persons.map(p => ({...p, _createNew: false, _firstname: '', _lastname: ''}))
        : [{person: null, authorType: null, _createNew: false, _firstname: '', _lastname: ''}],
});

// --- Computed ---

const currentTypeName = computed(() => {
    const t = config.payload.types.find(t => t.uid == form.type);
    return t ? t.name : '';
});

const submitUrl = computed(() => {
    return isNew ? config.urls.create : config.urls.update;
});

/**
 * Publisher payload for the mapper: the UID of the selected Verlag, or
 * {name, location} when a new one is being created. An unnamed new Verlag
 * serializes as null — the publisher is an optional field.
 *
 * Zeitschriftenaufsätze have no Verlag/Erscheinungsort at all — the fields are hidden
 * for that type, so a leftover value must not be submitted either.
 */
const publisherPayload = computed(() => {
    if (Number(form.type) === PublicationType.Zeitschriftenaufsatz) return null;
    if (!form._publisherCreateNew) return form.publisher;
    const name = form._publisherName.trim();
    if (!name) return null;
    return {name, location: form._publisherLocation.trim()};
});

/**
 * Serialize the form state into a JSON string for the hidden input.
 * - Keywords that are objects {uid, name} get serialized as just uid (number).
 * - Keywords that are strings get serialized as strings (create new).
 * - Publisher: existing → uid, new → {name, location}.
 * - Persons: existing → {person: uid, authorType: uid}, new → {person: {firstname, lastname}, authorType: uid}.
 * - Null/empty entries in collections are filtered out.
 */
const serializedData = computed(() => {
    const data = {
        uid: form.uid,
        type: form.type,
        title: form.title,
        subtitle: form.subtitle,
        parallelTitle: form.parallelTitle,
        favor: form.favor,
        uniformTitle: form.uniformTitle,
        secondaryTitle: form.secondaryTitle,
        otherTitle: form.otherTitle,
        series: form.series,
        volume: form.volume,
        magazine: form.magazine,
        magazineBook: form.magazineBook,
        magazineYear: form.magazineYear,
        publication: form.publication,
        publisher: publisherPayload.value,
        isbn: form.isbn,
        language: form.language,
        pages: form.pages,
        year: form.year,
        url: form.url,
        url2: form.url2,
        url2Addition: form.url2Addition,
        universityWriting: form.universityWriting,
        editionType: form.editionType,
        edition: form.edition,
        doi: form.doi,
        urn: form.urn,
        issn: form.issn,
        abstract: form.abstract,
        attached: form.attached,
        attachedWork: form.attachedWork,
        addionalInformation: form.addionalInformation,
        onlyWaldorf: form.onlyWaldorf,

        mediaTypes: form.mediaTypes.filter(v => v != null && v !== ''),
        keywords: form.keywords
            .map(kw => {
                if (kw === null || kw === undefined || kw === '') return null;
                if (typeof kw === 'object' && kw.uid) return kw.uid;
                return kw; // number or string
            })
            .filter(v => v != null && v !== ''),
        persons: form.persons
            .filter(p => p.person != null || p._createNew)
            .map(p => {
                if (p._createNew && (p._firstname || p._lastname)) {
                    return {
                        person: { firstname: p._firstname || '', lastname: p._lastname || '' },
                        authorType: p.authorType,
                    };
                }
                return { person: p.person, authorType: p.authorType };
            }),
    };
    return JSON.stringify(data);
});

// --- Validation ---

const submitted = ref(false);
const submitting = ref(false);
const formEl = ref(null);
const personsSectionEl = ref(null);
const confirmModal = ref(null);
let parsleyInstance = null;

// Provide submitted state to all descendant field components
provide('submitted', submitted);

// Field tooltips (form key => rendered HTML), filled from the descriptions record
provide('descriptions', config.descriptions || {});

onMounted(() => {
    const Parsley = window.Parsley;

    // Custom validator: recommended (soft validation — only triggers modal, not blocking)
    Parsley.addValidator('recommended', {
        messages: {
            de: 'Dieses Feld wird empfohlen auszufüllen',
        },
        priority: 512,
        validateMultiple: function (values) {
            return values.length > 0;
        },
        validateString: function (value) {
            return /\S/.test(value);
        },
    });

    // Initialize Parsley on the form
    const $form = $(formEl.value);
    parsleyInstance = $form.parsley({
        // Suppress Parsley's own error rendering — Vue handles error display
        errorsContainer: function () {
            return $('<div class="d-none">');
        },
    });

    // Remove Parsley's auto-submit handler — we call validate() manually in handleSubmit
    $form.off('submit.Parsley submit.parsley');
});

/**
 * Find the topmost element to scroll to on validation failure.
 *
 * Walks Parsley's field list directly — this catches every input Parsley validates,
 * including raw <input> fields not wrapped in FormField/FormSelect (which would
 * otherwise be missed by a plain `.is-invalid` selector).
 *
 * For hidden inputs (e.g. FormSelect's d-none mirror input), walks up to find a
 * visible ancestor so scrollIntoView actually does something useful.
 *
 * Person cardinality errors don't map to a single field, so we include the
 * persons section as a candidate when that error is present.
 */
function scrollToFirstError(cardinalityError) {
    const candidates = [];

    if (parsleyInstance) {
        for (const field of parsleyInstance.fields) {
            if (field.validationResult === true) continue;
            if (!Array.isArray(field.validationResult)) continue;
            const hasBlocking = field.validationResult.some(r => r.assert.name !== 'recommended');
            if (!hasBlocking) continue;

            let el = field.$element?.[0];
            if (!el) continue;
            // Hidden element (e.g. FormSelect's d-none input): use the visible ancestor
            if (el.offsetParent === null) {
                el = el.closest('.lfmpub-select, .mb-3') ?? el.parentElement ?? el;
            }
            if (el) candidates.push(el);
        }
    }

    if (cardinalityError && personsSectionEl.value) {
        candidates.push(personsSectionEl.value);
    }

    if (candidates.length === 0) {
        // Fallback: any element marked .is-invalid in DOM order
        const fallback = formEl.value?.querySelector('.is-invalid');
        if (fallback) fallback.scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
    }

    // Pick the topmost candidate (smallest top in viewport coordinates)
    candidates.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    candidates[0].scrollIntoView({behavior: 'smooth', block: 'center'});
}

async function handleSubmit() {
    submitted.value = true;
    await nextTick();

    // Per-AuthorType min/max — payload-driven (enum + #[Cardinality]).
    let hasPersonCardinalityError = false;
    {
        const validPersons = form.persons.filter(p => p.person != null || p._createNew);
        if (validPersons.length > 0) {
            const counts = {};
            validPersons.forEach(p => {
                const t = p.authorType == null ? null : Number(p.authorType);
                if (t !== null) counts[t] = (counts[t] ?? 0) + 1;
            });
            for (const at of (config.payload.authorTypes || [])) {
                const c = counts[Number(at.uid)] ?? 0;
                if (at.minCount >= 1 && c < at.minCount) {
                    hasPersonCardinalityError = true;
                    break;
                }
                if (at.maxCount != null && c > at.maxCount) {
                    hasPersonCardinalityError = true;
                    break;
                }
            }
        }
    }

    // --- Parsley validation ---
    let hasRequiredErrors = hasPersonCardinalityError;
    let hasRecommendedErrors = false;

    if (parsleyInstance) {
        // Re-scan DOM so Parsley picks up any attribute changes from Vue reactivity
        parsleyInstance.refresh();
        parsleyInstance.validate();

        parsleyInstance.fields.forEach(field => {
            if (field.validationResult === true) return;
            if (!Array.isArray(field.validationResult)) return;
            field.validationResult.forEach(result => {
                if (result.assert.name === 'recommended') {
                    hasRecommendedErrors = true;
                } else {
                    hasRequiredErrors = true;
                }
            });
        });
    }

    if (hasRequiredErrors) {
        nextTick(() => scrollToFirstError(hasPersonCardinalityError));
        return;
    }

    if (hasRecommendedErrors && confirmModal.value) {
        const result = await confirmModal.value.open({
            title: 'Eingabe unvollständig',
            message:
                'Sie haben nicht alle empfohlenen Felder ausgefüllt.<br>Möchten Sie den Datensatz trotzdem speichern?',
        });
        if (result !== 'accepted') return;
    }

    submitting.value = true;
    // Submit the actual form (with the hidden JSON field)
    formEl.value.submit();
}

async function handleDelete() {
    if (!confirmModal.value) return;
    const result = await confirmModal.value.open({
        title: 'Datensatz löschen',
        message: `Möchten Sie den Datensatz "${form.title}" wirklich löschen?`,
    });
    if (result !== 'accepted') return;

    // Submit to delete URL
    formEl.value.action = config.urls.delete;
    submitting.value = true;
    formEl.value.submit();
}

// When a Sammelwerksbeitrag picks its parent Sammelwerk, mirror the parent's
// year / edition / editionType into the Beitrag — these are shown read-only.
// Fires on user-initiated parent change, not on initial mount (existing records keep their stored values).
watch(
    () => form.publication,
    (newParent) => {
        if (Number(form.type) !== PublicationType.Sammelwerksbeitrag) return;
        if (newParent == null || newParent === '') return;
        const parent = (config.payload.sammelwerke || []).find(s => s.uid == newParent);
        if (!parent) return;
        form.year = parent.year ?? form.year;
        form.edition = parent.edition ?? form.edition;
        form.editionType = parent.editionType ?? form.editionType;
    }
);

async function onTypeChange(newType) {
    if (newType == form.type) return;

    // Monographie and Sammelwerk share the same field set — no warning needed.
    const monoSammelwerk = new Set([PublicationType.Monographie, PublicationType.Sammelwerk]);
    const fieldsIdentical = monoSammelwerk.has(Number(form.type)) && monoSammelwerk.has(Number(newType));

    if (!fieldsIdentical && confirmModal.value) {
        await confirmModal.value.open({
            title: 'Info',
            message: 'Durch die Änderung des Typs können sich die Formularfelder inhaltlich ändern.',
            mode: 'info',
        });
    }
    form.type = newType;
}
</script>
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                     